import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { img_url } from '../../api/index';
import { useBodyScrollLock } from './useBodyScrollLock';

export interface QrStoryItem {
  image?: string | null;
  title?: string | null;
  description?: string | null;
}

export interface QrStory {
  id: number | string;
  title?: string | null;
  image?: string | null;
  items?: QrStoryItem[] | null;
}

interface MenuStoriesProps {
  stories: QrStory[];
}

const getImageUrl = (img?: string | null) => {
  if (!img) return '';
  return img.startsWith('http') ? img : `${img_url}/${img}`;
};

export default function MenuStories({ stories }: MenuStoriesProps) {
  const [selectedStory, setSelectedStory] = useState<QrStory | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useBodyScrollLock(!!selectedStory);

  useEffect(() => {
    if (!selectedStory) return;
    const items = selectedStory.items || [];
    if (items.length === 0) return;
    const t = setTimeout(() => {
      if (currentIndex < items.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        nextStory();
      }
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStory, currentIndex]);

  if (!stories || stories.length === 0) return null;

  const openStory = (story: QrStory) => {
    setSelectedStory(story);
    setCurrentIndex(0);
  };

  const closeStory = () => {
    setSelectedStory(null);
    setCurrentIndex(0);
  };

  const nextStory = () => {
    const idx = stories.findIndex((s) => s.id === selectedStory?.id);
    if (idx < stories.length - 1) {
      setSelectedStory(stories[idx + 1]);
      setCurrentIndex(0);
    } else {
      closeStory();
    }
  };

  const prevStory = () => {
    const idx = stories.findIndex((s) => s.id === selectedStory?.id);
    if (idx > 0) {
      setSelectedStory(stories[idx - 1]);
      setCurrentIndex(0);
    }
  };

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4 px-5 scrollbar-hide">
        {stories.map((story) => {
          const url = getImageUrl(story.image);
          return (
            <button
              key={story.id}
              type="button"
              onClick={() => openStory(story)}
              className="flex flex-col items-center gap-2.5 flex-shrink-0 group w-[85px] qrm-no-select"
            >
              <div className="relative w-[76px] h-[84px]">
                <div className="absolute inset-0 rounded-[24px] bg-gradient-to-tr from-emerald-400 to-teal-500 p-[2px] shadow-sm group-hover:scale-105 transition-transform duration-300">
                  <div className="w-full h-full rounded-[22px] bg-white dark:bg-[#09090b] p-[2px]">
                    {url ? (
                      <img
                        src={url}
                        alt={story.title || ''}
                        className="w-full h-full rounded-[18px] object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-[18px] bg-gradient-to-br from-orange-200 to-amber-200 flex items-center justify-center text-2xl">
                        🍽️
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-200 text-center w-full truncate">
                {story.title}
              </span>
            </button>
          );
        })}
      </div>

      {selectedStory && (
        <div className="fixed inset-0 bg-black z-[80] flex items-center justify-center animate-fade-in">
          <div className="absolute top-4 left-4 right-4 flex gap-1 z-20">
            {(selectedStory.items || []).map((_, idx) => (
              <div
                key={idx}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                {idx < currentIndex && (
                  <div className="h-full bg-white rounded-full w-full" />
                )}
                {idx === currentIndex && (
                  <div className="h-full bg-white rounded-full animate-progress-3s" />
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={closeStory}
            className="absolute top-8 right-4 text-white z-20 bg-black/30 backdrop-blur-md rounded-full p-2"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={prevStory}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white z-20 bg-black/20 backdrop-blur-md rounded-full p-3 hidden sm:block"
            aria-label="previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={nextStory}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white z-20 bg-black/20 backdrop-blur-md rounded-full p-3 hidden sm:block"
            aria-label="next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="relative w-full h-full max-w-md mx-auto bg-gray-900 overflow-hidden sm:rounded-[40px] sm:h-[90vh] sm:my-auto sm:border-8 sm:border-gray-800">
            {(() => {
              const items = selectedStory.items || [];
              const item = items[currentIndex] || items[0];
              if (!item) return null;
              const itemUrl = getImageUrl(item.image);
              return (
                <>
                  {itemUrl ? (
                    <img
                      src={itemUrl}
                      alt={item.title || ''}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-orange-400 to-amber-500" />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                    {item.title ? (
                      <h3 className="text-white text-2xl font-bold mb-2 tracking-tight">
                        {item.title}
                      </h3>
                    ) : null}
                    {item.description ? (
                      <p className="text-white/90 text-[15px] leading-relaxed">
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                </>
              );
            })()}

            <div
              onClick={prevStory}
              className="absolute left-0 top-0 bottom-0 w-1/3 cursor-pointer z-10"
            />
            <div
              onClick={nextStory}
              className="absolute right-0 top-0 bottom-0 w-1/3 cursor-pointer z-10"
            />
          </div>
        </div>
      )}
    </>
  );
}
